import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useAuth } from '@/contexts/auth-context';
import { collectionsApi } from '@/lib/api/collections';
import { CollectionDataPanel } from '@/components/shared/collection-data-panel';
import type { CollectionWithDetails, ResourceOperation } from '@/types/entities';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

const ALL_OPERATIONS: ResourceOperation[] = ['create', 'update', 'delete'];

export function CollectionDataPage() {
  const { collectionName, collection_item_id } = useParams<{ collectionName: string; collection_item_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const { user } = useAuth();
  const tenant = user?.tenant;
  
  const [collection, setCollection] = useState<CollectionWithDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const editorMode = location.pathname.endsWith('/create')
    ? 'create'
    : location.pathname.endsWith('/edit')
      ? 'edit'
      : null;

  const editingItemId = collection_item_id ? Number(collection_item_id) : null;

  const allowedOps = collection?.details.ops?.length
    ? Array.from(new Set<ResourceOperation>(collection.details.ops))
    : ALL_OPERATIONS;

  useEffect(() => {
    if (!collectionName || !tenant?.id || !tenant?.slug) {
      setError('Collection name or tenant not available');
      setIsLoading(false);
      return;
    }

    const fetchCollection = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Fetch all collections for this tenant using admin API
        const collections = await collectionsApi.getAllForTenant(tenant.slug);
        const foundCollection = collections.find(c => c.name === collectionName);

        if (!foundCollection) {
          setError(`Collection "${collectionName}" not found`);
          setIsLoading(false);
          return;
        }

        // Get full collection details with schema
        const fullCollection = await collectionsApi.getById(tenant.slug, foundCollection.id);
        const details = JSON.parse(fullCollection.details) as CollectionWithDetails['details'];
        setCollection({ ...fullCollection, details });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load collection');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCollection();
  }, [collectionName, tenant?.id, tenant?.slug]);

  if (isLoading) {
    return <div className="p-12 text-center text-muted-foreground">Loading...</div>;
  }

  if (error || !collection) {
    return (
      <div className="p-12 text-center text-destructive">
        {error || 'Collection not found'}
      </div>
    );
  }

  if (editorMode === 'create' && !allowedOps.includes('create')) {
    return <div className="p-12 text-center text-destructive">Create is disabled for this collection</div>;
  }

  if (editorMode === 'edit' && !allowedOps.includes('update')) {
    return <div className="p-12 text-center text-destructive">Update is disabled for this collection</div>;
  }

  return (
    <>
      {!editorMode && (
        <PageHeader
          title={collection.label || collection.name}
          description="Manage content data"
        />
      )}
      <PageSection>
        {tenant && (
          <CollectionDataPanel
            tenant={tenant}
            collection={collection}
            enforceAccess
            editorMode={editorMode}
            editingItemId={editingItemId}
            onCreateRoute={() => navigate(`/tenant/dashboard/collections/${collection.name}/create`)}
            onEditRoute={(itemId) => navigate(`/tenant/dashboard/collections/${collection.name}/${itemId}/edit`)}
            onExitEditorRoute={() => navigate(`/tenant/dashboard/collections/${collection.name}`)}
          />
        )}
      </PageSection>
    </>
  );
}
